import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
// import axios from 'axios';
import { Button, CustomSelect, FormField, Input } from '@/components';
import { handleApiError } from '@/common/utils';
import { registerCompany } from '../../api';
import {
  AUTH_BUTTON_CLASS,
  AUTH_FORM_CARD_CLASS,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
} from '../../lib/authStyles';
import { COUNTRY_OPTIONS } from '@/modules/dashboard';

export function CompanyRegistrationForm({ onBack, onSuccess }) {
  const [formData, setFormData] = useState({
    companyName: '',
    address: '',
    registrationNumber: '',
    industry: '',
    companySize: '',
    pincode: '',
    district: '',
    state: '',
    country: '',
  });

  // const [isLoadingPincode, setIsLoadingPincode] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handlePincodeChange = async (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);

    if (fieldErrors.pincode || fieldErrors.district || fieldErrors.state) {
      setFieldErrors((prev) => ({
        ...prev,
        pincode: '',
        district: '',
        state: '',
      }));
    }

    setFormData((prev) => ({
      ...prev,
      pincode: value,
    }));

    // if (value.length === 6) {
    //   setIsLoadingPincode(true);
    //   try {
    //     const response = await axios.get(
    //       `https://api.postalpincode.in/pincode/${value}`,
    //     );
    //     const data = response.data;

    //     if (
    //       data &&
    //       data[0] &&
    //       data[0].Status === 'Success' &&
    //       data[0].PostOffice &&
    //       data[0].PostOffice.length > 0
    //     ) {
    //       const postOffice = data[0].PostOffice[0];
    //       setFormData((prev) => ({
    //         ...prev,
    //         district: postOffice.District || '',
    //         state: postOffice.State || '',
    //         country: postOffice.Country || '',
    //       }));
    //       toast.success('Pincode details fetched!');
    //     } else {
    //       toast.error('Invalid Pincode or no details found.');
    //     }
    //   } catch (error) {
    //     console.error('Error fetching pincode details:', error);
    //     toast.error('Failed to fetch pincode details.');
    //   } finally {
    //     setIsLoadingPincode(false);
    //   }
    // }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: registerCompany,
    onSuccess: (data) => {
      onSuccess(data);
    },
    onError: (err) => {
      const errorData = err.response?.data;
      if (
        errorData &&
        typeof errorData === 'object' &&
        !Array.isArray(errorData)
      ) {
        const newErrors = {};

        // Parse root fields
        if (errorData.name) {
          newErrors.companyName = Array.isArray(errorData.name)
            ? errorData.name[0]
            : errorData.name;
        }
        if (errorData.industry_type) {
          newErrors.industry = Array.isArray(errorData.industry_type)
            ? errorData.industry_type[0]
            : errorData.industry_type;
        }
        if (errorData.company_size) {
          newErrors.companySize = Array.isArray(errorData.company_size)
            ? errorData.company_size[0]
            : errorData.company_size;
        }

        // Parse nested address fields
        if (
          errorData.address &&
          typeof errorData.address === 'object' &&
          !Array.isArray(errorData.address)
        ) {
          if (errorData.address.line1) {
            newErrors.address = Array.isArray(errorData.address.line1)
              ? errorData.address.line1[0]
              : errorData.address.line1;
          }
          if (errorData.address.postal_code) {
            newErrors.pincode = Array.isArray(errorData.address.postal_code)
              ? errorData.address.postal_code[0]
              : errorData.address.postal_code;
          }
          if (errorData.address.city) {
            newErrors.district = Array.isArray(errorData.address.city)
              ? errorData.address.city[0]
              : errorData.address.city;
          }
          if (errorData.address.state) {
            newErrors.state = Array.isArray(errorData.address.state)
              ? errorData.address.state[0]
              : errorData.address.state;
          }
          if (errorData.address.country) {
            newErrors.country = Array.isArray(errorData.address.country)
              ? errorData.address.country[0]
              : errorData.address.country;
          }
        }

        // Parse nested tax details
        if (
          errorData.tax_details &&
          typeof errorData.tax_details === 'object' &&
          !Array.isArray(errorData.tax_details)
        ) {
          if (errorData.tax_details.tax_id) {
            newErrors.registrationNumber = Array.isArray(
              errorData.tax_details.tax_id,
            )
              ? errorData.tax_details.tax_id[0]
              : errorData.tax_details.tax_id;
          }
        }

        setFieldErrors(newErrors);
        toast.error('Please correct the errors in the form.');
      } else {
        handleApiError(err, 'Failed to register company.');
      }
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = {
      name: formData.companyName,
      address: {
        line1: formData.address || '',
        line2: '',
        city: formData.district || '',
        state: formData.state || '',
        postal_code: formData.pincode || '',
        country: formData.country ? formData.country.toUpperCase() : '',
      },
      tax_details: {
        tax_id: formData.registrationNumber,
        tax_type: 'gst',
        registration_number: formData.registrationNumber,
      },
      industry_type: formData.industry,
      company_size: formData.companySize,
    };

    mutate(payload);
  };

  return (
    <div className={`${AUTH_FORM_CARD_CLASS} text-left`}>
      <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4">
        {/* Legal Company Name */}
        <FormField
          label="Legal Company Name"
          htmlFor="companyName"
          error={fieldErrors.companyName}
          labelClassName={AUTH_LABEL_CLASS}
        >
          <Input
            size="lg"
            type="text"
            id="companyName"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            placeholder="e.g. Acme Corp Inc."
            error={!!fieldErrors.companyName}
            className={AUTH_INPUT_CLASS}
            disabled={isPending}
          />
        </FormField>

        {/* Business Address */}
        <FormField
          label="Business Address"
          htmlFor="address"
          error={fieldErrors.address}
          labelClassName={AUTH_LABEL_CLASS}
        >
          <Input
            size="lg"
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="Street address, building, office suite, etc."
            error={!!fieldErrors.address}
            className={AUTH_INPUT_CLASS}
            disabled={isPending}
          />
        </FormField>

        {/* Pincode & District Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            label="Pincode"
            htmlFor="pincode"
            error={fieldErrors.pincode}
            labelClassName={AUTH_LABEL_CLASS}
          >
            <div className="relative w-full">
              <Input
                size="lg"
                type="text"
                id="pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handlePincodeChange}
                placeholder="e.g. 395006"
                error={!!fieldErrors.pincode}
                disabled={isPending}
                maxLength={6}
                className={AUTH_INPUT_CLASS}
              />
            </div>
          </FormField>

          <FormField
            label="District"
            htmlFor="district"
            error={fieldErrors.district}
            labelClassName={AUTH_LABEL_CLASS}
          >
            <Input
              size="lg"
              type="text"
              id="district"
              name="district"
              value={formData.district}
              onChange={handleChange}
              placeholder="District"
              error={!!fieldErrors.district}
              className={AUTH_INPUT_CLASS}
              disabled={isPending}
            />
          </FormField>
        </div>

        {/* State & Country Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            label="State"
            htmlFor="state"
            error={fieldErrors.state}
            labelClassName={AUTH_LABEL_CLASS}
          >
            <Input
              size="lg"
              type="text"
              id="state"
              name="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="State"
              error={!!fieldErrors.state}
              className={AUTH_INPUT_CLASS}
              disabled={isPending}
            />
          </FormField>

          <FormField
            label="Country"
            htmlFor="country"
            error={fieldErrors.country}
            labelClassName={AUTH_LABEL_CLASS}
          >
            <CustomSelect
              value={formData.country}
              onChange={(val) =>
                handleChange({ target: { name: 'country', value: val } })
              }
              options={COUNTRY_OPTIONS}
              placeholder="Select country"
              searchable={true}
              searchPlaceholder="Search country..."
              disabled={isPending}
              className="w-full h-[52px]"
              textSizeClass={AUTH_INPUT_CLASS}
              error={!!fieldErrors.country}
            />
          </FormField>
        </div>

        {/* Registration Number */}
        <FormField
          label="Registration Number (GST/PAN)"
          htmlFor="registrationNumber"
          error={fieldErrors.registrationNumber}
          labelClassName={AUTH_LABEL_CLASS}
        >
          <Input
            size="lg"
            type="text"
            id="registrationNumber"
            name="registrationNumber"
            value={formData.registrationNumber}
            onChange={handleChange}
            placeholder="e.g. GB123456789"
            error={!!fieldErrors.registrationNumber}
            className={AUTH_INPUT_CLASS}
            disabled={isPending}
          />
        </FormField>

        {/* Industry & Company Size Select Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            label="Industry"
            htmlFor="industry"
            error={fieldErrors.industry}
            labelClassName={AUTH_LABEL_CLASS}
          >
            <CustomSelect
              value={formData.industry}
              onChange={(val) =>
                handleChange({ target: { name: 'industry', value: val } })
              }
              options={[
                { value: 'technology', label: 'Technology' },
                { value: 'retail', label: 'Retail' },
                { value: 'healthcare', label: 'Healthcare' },
                { value: 'finance', label: 'Finance' },
                { value: 'manufacturing', label: 'Manufacturing' },
                { value: 'education', label: 'Education' },
                { value: 'other', label: 'Other' },
              ]}
              placeholder="Select industry"
              disabled={isPending}
              className="w-full h-[52px]"
              textSizeClass={AUTH_INPUT_CLASS}
              error={!!fieldErrors.industry}
            />
          </FormField>

          <FormField
            label="Company Size"
            htmlFor="companySize"
            error={fieldErrors.companySize}
            labelClassName={AUTH_LABEL_CLASS}
          >
            <CustomSelect
              value={formData.companySize}
              onChange={(val) =>
                handleChange({ target: { name: 'companySize', value: val } })
              }
              options={[
                { value: 'size_1_10', label: '1-10 employees' },
                { value: 'size_11_50', label: '11-50 employees' },
                { value: 'size_51_200', label: '51-200 employees' },
                { value: 'size_201_500', label: '201-500 employees' },
                { value: 'size_501_plus', label: '501+ employees' },
              ]}
              placeholder="Employee count"
              disabled={isPending}
              className="w-full h-[52px]"
              textSizeClass={AUTH_INPUT_CLASS}
              error={!!fieldErrors.companySize}
            />
          </FormField>
        </div>

        {/* Submit / Continue Button */}
        <div className="flex justify-center mt-6">
          <Button
            size="lg"
            type="submit"
            variant="primary"
            className={`${AUTH_BUTTON_CLASS} w-[230px] h-[48px]`}
            disabled={isPending}
          >
            {isPending ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-5 w-5 text-surface-mint"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Saving...
              </span>
            ) : (
              <>
                <span>Save & Continue</span>
                <span>→</span>
              </>
            )}
          </Button>
        </div>

        <div className="text-center mt-4">
          <button
            type="button"
            onClick={onBack}
            className="text-body-lg font-normal font-sf-pro-display text-text-primary hover:text-primary transition-colors cursor-pointer inline-flex items-center gap-1.5"
          >
            <svg
              className="w-4 h-4 inline"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
            <span>Back to Login</span>
          </button>
        </div>
      </form>
    </div>
  );
}
